/**
 * Admin · Create Booking on behalf of customer
 *
 * Backed by `POST /api/admin/bookings` (already shipped). Renders a modal
 * with the same key fields as the public booking form plus admin-only
 * controls (payment_mode, send_email toggle, notes).
 *
 * Uses the existing AU-only `AddressAutocomplete` so address handling is
 * identical to the public flow — no duplicate Google loader, formatted
 * AU address saved.
 *
 * Hands back the created booking so the caller can immediately offer
 * "Download sample label" / "View booking" actions on success.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Download, Mail, FilePlus, ClipboardList, Info, Package } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PropertyIntakeForm, intakeFormToPayload, validateIntakeForm } from "@/components/PropertyIntakeForm";
import api, { formatApiError } from "@/lib/api";
import { TESTS, ONLINE_PREPAID_DISCOUNT_PCT } from "@/lib/siteData";
import { usePublicPricing } from "@/lib/usePublicPricing";
// Phase 2L-A144 — reuse the same AU-address parser as the public
// Booking flow so the "Use property address" preview populates
// suburb/state/postcode identically for admin/lab-tech-created
// phone bookings.
import { parseAusAddress } from "@/lib/serviceArea";

// Questionnaire enums — MUST stay in lock-step with `/app/frontend/src/pages/Booking.jsx`
// REASON_FOR_TESTING_OPTIONS / PRIMARY_WATER_USE_OPTIONS so admin-created
// bookings produce identical downstream data (Pristine Summary labels,
// recommendation_engine reason gating, etc.).
const REASON_FOR_TESTING_OPTIONS = [
  { value: "routine", label: "Routine water quality check" },
  { value: "drinking_safety", label: "Drinking water safety check" },
  { value: "property_sale", label: "Property sale or purchase" },
  { value: "rental", label: "Rental property / property management" },
  { value: "new_system", label: "New tank or water system check" },
  { value: "taste_odour_colour_staining", label: "Taste, odour, colour, or staining concern" },
  { value: "illness_health", label: "Illness or health-related concern" },
  { value: "other", label: "Other" },
];
const PRIMARY_WATER_USE_OPTIONS = [
  { value: "drinking", label: "Drinking water" },
  { value: "household", label: "Household domestic use" },
  { value: "irrigation", label: "Irrigation / garden use" },
  { value: "livestock", label: "Livestock / animals" },
  { value: "pool_spa", label: "Pool or spa top-up" },
  { value: "commercial", label: "Commercial or workplace use" },
  { value: "other", label: "Other" },
];

// Single source of truth: the same `TESTS` array used by the public booking
// page (`/app/frontend/src/lib/siteData.js`), which mirrors the backend
// canonical catalog (`/api/pricing` from `core/constants.py`). This guarantees
// admin/lab tech see the same effective customer-facing prices — `retail`
// (strike-through) and `online_prepaid` (the price actually charged after
// the universal 20% online prepaid discount).
const TEST_CATALOG = TESTS.map((t) => ({
  id: t.id,
  name: t.name,
  retail: t.retail,
  online_prepaid: t.online_prepaid,
  default_sample: t.id === "bore" ? "bore-water" : "tank-rainwater",
}));

// `EXPERT_ADVICE_FEE` retired Feb-2026 — kept as 0 for any legacy callers
// that still pass the field. UI no longer offers the add-on.
const EXPERT_ADVICE_FEE = 0;

const SAMPLE_TYPES = [
  { value: "tank-rainwater", label: "Rainwater tank" },
  { value: "bore-water",     label: "Bore water" },
  { value: "spring",         label: "Spring / surface" },
  // town-water + filtered removed from public + admin booking flow
  // Feb-2026. Aware currently focuses on private water supplies only.
];

// Payment modes — Phase 2L-A64: lab techs now have access to the same
// set as admins so they can mark walk-in / phone-in bookings as already
// paid or complimentary without needing an admin to redo the booking.
// The booking's audit_log records `user_role` so the source of any
// already-paid / comp booking remains traceable.
const PAYMENT_MODES = [
  { value: "pay_link", label: "Pay link · pending until paid" },
  { value: "invoice",  label: "Invoice to follow" },
  { value: "paid",     label: "Mark as already paid" },
  { value: "comp",     label: "Complimentary / no charge" },
];

function Field({ label, required, children, hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </Label>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export function CreateBookingModal({ open, onClose, currentUser, onCreated, initialCustomer }) {
  // Feb 2026 — Central Online Discount switch. Admin-created bookings
  // that are attributed to a public flow (no partner selected, non-PM
  // customer) surface the "Online prepaid (save X%)" copy; when the
  // switch is off we drop the label and strike-through so admins see
  // the same clean layout customers do.
  const { enabled: discountEnabled, pct: discountPct } = usePublicPricing();
  const PUBLIC_DISCOUNT_LABEL_LIVE = discountEnabled && discountPct > 0
    ? `Online prepaid (save ${discountPct}%)`
    : "Standard price";
  // ---- Form state
  const [test, setTest] = useState(TEST_CATALOG[1]); // default to tank — most common
  // Expert advice add-on retired Feb-2026 — kept as a const `false` so the
  // submit payload shape is unchanged (back-compat with any historical
  // server-side handlers).
  const expertAdvice = false;
  const [customer, setCustomer] = useState({
    name: "", mobile: "", email: "", address: "", sampleType: TEST_CATALOG[1].default_sample,
    reason_for_testing: "", reason_for_testing_other: "",
    primary_water_use: "", primary_water_use_other: "",
  });
  const [paymentMode, setPaymentMode] = useState("pay_link");
  const [sendEmail, setSendEmail] = useState(true);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // populated on success

  // Phase 2L-A31 — sample identification (optional). Same shape as
  // the public booking form so the COA, sample labels and lab queue
  // search all show the same values regardless of how the booking
  // was created.
  const [customerSampleReference, setCustomerSampleReference] = useState("");
  const [samplePoint, setSamplePoint] = useState("");

  // Phase 2L-A10 — Customer Questionnaire (mirrors the public Booking flow).
  // When `skipQuestionnaire` is false (default), the modal collects the same
  // reason_for_testing + primary_water_use + Property & Water System
  // Information stepper that public customers complete. When true, the
  // staff member captures a short reason for the bypass which is written
  // to the booking audit trail (server-side: `questionnaire_skipped`,
  // `_skipped_by`, `_skipped_at`, `_skipped_reason`).
  const [intake, setIntake] = useState(null);
  const [skipQuestionnaire, setSkipQuestionnaire] = useState(false);
  const [skipReason, setSkipReason] = useState("");

  // Phase 2L-A5: discount code applied to the booking. Uses the same
  // `POST /api/discounts/validate` endpoint as the public booking page
  // so validation rules are identical (active, within window, usage_limit,
  // per_customer_limit, min_cart_value, applicable_test_ids).
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null);  // {code, type, value, amount, final_total}
  const [discountErr, setDiscountErr] = useState("");
  const [discountBusy, setDiscountBusy] = useState(false);

  // ── Phase 2L-A144 — Paid posted sample-kit (Feb 2026) ───────────────
  // Mirrors the public Booking flow so phone-in / walk-in bookings
  // capture the same delivery address + fee + Starshipit hand-off. UI
  // only renders when the `posted_kit_paid` feature flag is visible
  // for the current staff member. Server re-validates + re-resolves
  // the fee — client-supplied values are never trusted.
  const [postedKit, setPostedKit] = useState({ enabled: false });
  // `lab_pickup` = customer collects / drops off the kit at the lab
  // (no fee, no shipping). `mail_out` = Aware posts a kit to the
  // customer's address (fee + Starshipit fulfilment). Default to
  // `mail_out` to match the current server-side default and the
  // public booking flow.
  // Feb-2026 bugfix — posted-kit is OPT-IN. Default to
  // `local_pickup_dropoff` so admins don't silently attach the fee to
  // every new admin-created booking. Matches the public flow default.
  const [kitMethod, setKitMethod] = useState("local_pickup_dropoff");
  const [selfSampleDiscount, setSelfSampleDiscount] = useState(false);
  const [postalAddressSource, setPostalAddressSource] = useState("property");
  const [postedKitAddress, setPostedKitAddress] = useState({
    line1: "", line2: "", suburb: "", state: "", postcode: "", country: "Australia",
  });
  const [postedKitDeliveryNotes, setPostedKitDeliveryNotes] = useState("");

  // Fetch posted-kit visibility + fee/image once when the modal opens.
  // Falls back to `{enabled:false}` on any error so the modal never
  // gets stuck loading.
  useEffect(() => {
    if (!open) return;
    // Optional caller-supplied prefill (e.g. Property Campaign lead conversion).
    if (initialCustomer) {
      setCustomer((c) => ({
        ...c,
        name: initialCustomer.name || c.name,
        mobile: initialCustomer.mobile || c.mobile,
        email: initialCustomer.email || c.email,
        address: initialCustomer.address || c.address,
      }));
    }
    let cancelled = false;
    api.get("/public/posted-kit-settings")
      .then(({ data }) => { if (!cancelled) setPostedKit(data || { enabled: false }); })
      .catch(() => { if (!cancelled) setPostedKit({ enabled: false }); });
    return () => { cancelled = true; };
  }, [open]);

  // Whenever the AddressAutocomplete emits a free-form string (either
  // while the user types OR when they pick a Google Places suggestion),
  // split it into structured fields so admins don't have to retype
  // suburb / state / postcode. Manual edits still work — the parser
  // only fills BLANK components.
  const setPostalAddressFromAutocomplete = (raw, setter) => {
    const parsed = parseAusAddress(raw || "");
    setter((prev) => ({
      line1:    raw || "",
      line2:    prev.line2 || "",
      suburb:   parsed.suburb   || prev.suburb   || "",
      state:    parsed.state    || prev.state    || "",
      postcode: parsed.postcode || prev.postcode || "",
      country:  parsed.country  || prev.country  || "Australia",
    }));
  };

  // Phase 2L-A64 — lab techs now see all four payment modes too. The
  // backend audit log captures `user_role` so already-paid / comp
  // bookings remain traceable to the actor that created them. The
  // public-facing field set is identical for admin and lab tech.
  void currentUser;
  const modes = PAYMENT_MODES;

  // Reset discount state when the modal closes/opens or the test changes.
  useEffect(() => {
    setDiscountInput("");
    setDiscount(null);
    setDiscountErr("");
  }, [open, test.id]);

  // Live price breakdown. Feb-2026 bugfix — this now respects the
  // central Online Discount switch. When the admin has disabled the
  // switch (`discountEnabled === false`), admin-created bookings use
  // retail pricing with NO online-prepaid treatment, mirroring the
  // public booking page. Previously we always used `online_prepaid`
  // regardless of the switch, so admin bookings silently kept the 20%
  // off after the toggle was turned off.
  //
  //   discount ON  → base = test.online_prepaid, savings line surfaces
  //   discount OFF → base = test.retail,          savings line = 0
  //
  // Discount code (if applied) subtracts from `publicBookingPrice`.
  // Server is authoritative — admin_create_booking re-validates and
  // re-resolves the canonical price from /api/pricing.
  const standardPackagePrice = useMemo(() => {
    const base = test?.retail || 0;
    return base + (expertAdvice ? EXPERT_ADVICE_FEE : 0);
  }, [test, expertAdvice]);
  const publicBookingPrice = useMemo(() => {
    const base = discountEnabled
      ? (Number(test?.online_prepaid ?? test?.retail) || 0)
      : (Number(test?.retail) || 0);
    return base + (expertAdvice ? EXPERT_ADVICE_FEE : 0);
  }, [test, expertAdvice, discountEnabled]);
  const publicBookingDiscountAmount = useMemo(
    () => Math.max(0, Math.round((standardPackagePrice - publicBookingPrice) * 100) / 100),
    [standardPackagePrice, publicBookingPrice],
  );
  const discountCodeDiscountAmount = useMemo(() => {
    if (!discount) return 0;
    return Math.max(0, Math.min(Number(discount.amount) || 0, publicBookingPrice));
  }, [discount, publicBookingPrice]);

  // Phase 2L-A144 — posted-kit fee added as a separate line item, in
  // parity with the public /payments/checkout flow. Only surfaces
  // when the flag is on AND the admin picked "post a kit to customer".
  const postedKitFeeAmount = useMemo(() => {
    if (!(postedKit.enabled && kitMethod === "mail_out")) return 0;
    return Math.max(0, (Number(postedKit.fee_cents) || 0) / 100);
  }, [postedKit, kitMethod]);

  const selfSampleEligible = ["local_pickup_dropoff", "existing_bottle", "admin_supplied"].includes(kitMethod);
  const selfSampleDiscountAmount = useMemo(
    () => (selfSampleEligible && selfSampleDiscount ? 20 : 0),
    [selfSampleEligible, selfSampleDiscount],
  );

  const finalAmountPayable = useMemo(
    () => Math.max(0, Math.max(0, publicBookingPrice - discountCodeDiscountAmount) + postedKitFeeAmount - selfSampleDiscountAmount),
    [publicBookingPrice, discountCodeDiscountAmount, postedKitFeeAmount, selfSampleDiscountAmount],
  );

  // Effective posted-kit delivery address the customer will see. When
  // the admin chose "use property address", we parse the sample/site
  // address they already entered — no retyping required. When they
  // chose "different postal address" we send whatever they typed into
  // the postal form directly.
  const effectivePostedKitAddress = useMemo(() => {
    if (postalAddressSource === "property") {
      const parsed = parseAusAddress(customer.address || "");
      return {
        line1:    parsed.line1 || (customer.address || ""),
        line2:    parsed.line2 || "",
        suburb:   parsed.suburb || "",
        state:    parsed.state || "",
        postcode: parsed.postcode || "",
        country:  parsed.country || "Australia",
      };
    }
    return {
      line1:    postedKitAddress.line1 || "",
      line2:    postedKitAddress.line2 || "",
      suburb:   postedKitAddress.suburb || "",
      state:    postedKitAddress.state || "",
      postcode: postedKitAddress.postcode || "",
      country:  postedKitAddress.country || "Australia",
    };
  }, [postalAddressSource, customer.address, postedKitAddress]);

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) {
      setDiscountErr("Please enter a discount code.");
      return;
    }
    setDiscountBusy(true);
    setDiscountErr("");
    try {
      const { data } = await api.post("/discounts/validate", {
        code,
        subtotal: publicBookingPrice,
        customer_email: customer.email || null,
        test_id: test.id,
      });
      if (data.valid) {
        setDiscount(data);
        setDiscountInput(data.code || code);
        setDiscountErr("");
      } else {
        setDiscount(null);
        setDiscountErr(data.message || "This discount code is not valid.");
      }
    } catch (e) {
      setDiscount(null);
      const msg = e.response?.data?.detail;
      setDiscountErr(typeof msg === "string" ? msg : "This discount code is not valid.");
    } finally {
      setDiscountBusy(false);
    }
  };

  const clearDiscount = () => {
    setDiscount(null);
    setDiscountInput("");
    setDiscountErr("");
  };

  const chooseTest = (testId) => {
    const t = TEST_CATALOG.find((x) => x.id === testId);
    if (!t) return;
    setTest(t);
    // Update sample type IF the customer hadn't manually overridden it,
    // i.e. it still matches the previous test's default.
    setCustomer((c) => {
      const prevDefault = TEST_CATALOG.find((x) => x.default_sample === c.sampleType);
      if (prevDefault) return { ...c, sampleType: t.default_sample };
      return c;
    });
  };

  const submit = async () => {
    // Validation
    if (!customer.name.trim()) return toast.error("Customer name required");
    if (!customer.email.trim()) return toast.error("Customer email required");
    if (!customer.mobile.trim()) return toast.error("Customer phone required");
    if (!customer.address.trim()) return toast.error("Property address required");
    // Questionnaire validation — matches public Booking.validateStep2()
    if (skipQuestionnaire) {
      if (!skipReason.trim()) {
        return toast.error("Please give a short reason for skipping the customer questionnaire");
      }
    } else {
      if (!customer.reason_for_testing) {
        return toast.error("Please select a reason for testing (or tick 'Skip customer questions')");
      }
      if (customer.reason_for_testing === "other" && !customer.reason_for_testing_other.trim()) {
        return toast.error("Please describe the reason for testing");
      }
      if (!customer.primary_water_use) {
        return toast.error("Please select the primary water use");
      }
      if (customer.primary_water_use === "other" && !customer.primary_water_use_other.trim()) {
        return toast.error("Please describe the primary water use");
      }
      if (!intake) {
        return toast.error("Please complete the Property & Water System Information section");
      }
      const intakeErr = validateIntakeForm(intake);
      if (intakeErr) return toast.error(intakeErr);
    }
    if (paymentMode === "comp") {
      // confirm before sending a $0 booking
      if (!window.confirm("Mark this booking as complimentary (no charge)?")) return;
    }
    // Phase 2L-A144 — posted-kit address validation.
    // Oct 2026 fix: validate (and later forward) the address for EVERY
    // mail-out booking, not only while the separate posted-kit fee flag
    // is on. Without an address a mail-out booking can never generate a
    // Starshipit label later (see SCWT-9D118C) — mirrors the Jun 2026
    // fix already applied to the public Booking.jsx flow.
    const isPostedKitFlow = kitMethod === "mail_out";
    if (isPostedKitFlow) {
      const a = effectivePostedKitAddress;
      if (!a.line1.trim() || !a.suburb.trim() || !a.postcode.trim()) {
        return toast.error(
          "Posted kit address needs street, suburb and postcode. "
          + "Check the property address or switch to 'Different postal address'."
        );
      }
    }
    setSubmitting(true);
    try {
      // When the questionnaire is skipped we still POST a clean customer
      // payload — the "_other" fields are cleared so they don't bleed into
      // downstream Pristine Summary labels.
      const customerPayload = skipQuestionnaire
        ? {
            name: customer.name,
            mobile: customer.mobile,
            email: customer.email,
            address: customer.address,
            business_name: customer.business_name?.trim() || null,
            sampleType: customer.sampleType,
            reason_for_testing: null,
            reason_for_testing_other: null,
            primary_water_use: null,
            primary_water_use_other: null,
          }
        : {
            ...customer,
            reason_for_testing_other:
              customer.reason_for_testing === "other" ? customer.reason_for_testing_other : null,
            primary_water_use_other:
              customer.primary_water_use === "other" ? customer.primary_water_use_other : null,
          };
      const { data } = await api.post("/admin/bookings", {
        test_id: test.id,
        test_name: test.name,
        base_price: test.retail,
        // Server treats `total` as the pre-discount subtotal when
        // discount_code is supplied. We send the public_booking_price
        // (== retail today; auto-promo aware in the future).
        total: publicBookingPrice,
        expert_advice: expertAdvice,
        customer: customerPayload,
        property_info: skipQuestionnaire ? null : intakeFormToPayload(intake),
        payment_mode: paymentMode,
        send_email: sendEmail,
        notes: notes.trim() || null,
        discount_code: discount ? discount.code : null,
        questionnaire_skipped: skipQuestionnaire,
        questionnaire_skipped_reason: skipQuestionnaire ? skipReason.trim() : null,
        // Phase 2L-A31 — optional sample identification (mirrors
        // public + PM booking flows). Strings only, server trims and
        // caps length.
        customer_sample_reference: customerSampleReference.trim() || null,
        sample_point: samplePoint.trim() || null,
        // Phase 2L-A144 — Paid posted sample-kit. Always forward the
        // chosen kit_method so the server-side resolver can pick
        // between mail_out / lab_pickup. Only forward the address +
        // notes when we're actually posting a kit (server ignores
        // otherwise). The server re-resolves the fee — never trust
        // the client.
        kit_method: kitMethod,
        self_sample_discount: selfSampleEligible && selfSampleDiscount,
        posted_kit_address: isPostedKitFlow
          ? {
              line1:    (effectivePostedKitAddress.line1 || "").trim(),
              line2:    (effectivePostedKitAddress.line2 || "").trim() || null,
              suburb:   (effectivePostedKitAddress.suburb || "").trim(),
              state:    (effectivePostedKitAddress.state || "").trim(),
              postcode: (effectivePostedKitAddress.postcode || "").trim(),
              country:  (effectivePostedKitAddress.country || "Australia").trim(),
            }
          : null,
        posted_kit_delivery_notes: isPostedKitFlow
          ? ((postedKitDeliveryNotes || "").trim() || null)
          : null,
      });
      setCreated(data);
      toast.success(`Booking ${data.reference} created${sendEmail ? " · email sent" : ""}`);
      onCreated?.(data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Your session has expired — please sign in again to create this booking.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to create booking");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // ============ Success screen ============
  if (created) {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const labelsUrl = `${backend}/api/bookings/${created.reference}/labels.pdf`;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
        <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-emerald-600 text-white px-6 py-5">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-100">Created</div>
            <h2 className="font-display text-2xl font-bold mt-1">Booking {created.reference}</h2>
          </div>
          <div className="p-6 space-y-3 text-sm">
            <div className="text-slate-700">
              <strong>{created.customer?.name}</strong> · {test.name} · ${finalAmountPayable.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Status: <strong className="uppercase">{created.status}</strong> · payment mode: <strong>{paymentMode}</strong>
              {sendEmail && created.customer?.email && (
                <span> · confirmation email queued to {created.customer.email}</span>
              )}
            </div>
            <div className="pt-3 grid grid-cols-2 gap-2">
              {created.lab_test_id && (
                <a
                  data-testid="created-booking-open-lab"
                  href={`/admin/lab/${created.lab_test_id}?focus=intake`}
                  className="rounded-full bg-cyan-600 text-white py-2 text-center text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-cyan-700 col-span-2"
                >
                  <FilePlus className="w-3.5 h-3.5" /> Open lab record
                </a>
              )}
              <a
                data-testid="created-booking-labels-link"
                href={labelsUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-slate-900 text-white py-2 text-center text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-slate-800"
              >
                <Download className="w-3.5 h-3.5" /> Sample label PDF
              </a>
              <a
                data-testid="created-booking-view-list"
                href={`/admin/bookings`}
                className="rounded-full border border-slate-300 text-slate-700 py-2 text-center text-xs font-semibold hover:bg-slate-50"
              >
                View bookings list
              </a>
            </div>
          </div>
          <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={() => { setCreated(null); }} className="text-xs font-semibold text-slate-600 hover:text-slate-900">Create another</button>
            <button data-testid="created-booking-close" onClick={() => { setCreated(null); onClose(); }} className="text-xs font-semibold text-slate-900 px-3 py-1.5 rounded-full bg-slate-200">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ============ Form ============
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 backdrop-blur-sm p-4">
      <div data-testid="create-booking-modal" className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl my-8 overflow-hidden">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Admin · Create Booking</div>
            <h2 className="font-display text-xl font-semibold text-slate-900 mt-0.5">Create booking on behalf of customer</h2>
          </div>
          <button data-testid="create-booking-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Test selection — prices mirror the public booking page */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {TEST_CATALOG.map((t) => {
              const hasOffer = (t.online_prepaid ?? t.retail) < t.retail;
              return (
                <button
                  key={t.id}
                  data-testid={`create-booking-test-${t.id}`}
                  onClick={() => chooseTest(t.id)}
                  className={`text-left p-3 rounded-2xl border-2 transition-colors ${
                    test.id === t.id ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t.id}</div>
                  <div className="font-semibold text-slate-900 text-sm leading-tight mt-1">{t.name}</div>
                  <div className="mt-1.5 flex items-baseline gap-1.5 flex-wrap">
                    {hasOffer && discountEnabled ? (
                      <>
                        <span className="text-sm font-bold text-slate-900 tabular-nums">${Number(t.online_prepaid).toFixed(2)}</span>
                        <span className="text-[10px] text-slate-400 line-through tabular-nums">${Number(t.retail).toFixed(2)}</span>
                      </>
                    ) : (
                      <span className="text-sm font-bold text-slate-900 tabular-nums">${Number(t.retail).toFixed(2)}</span>
                    )}
                  </div>
                  {hasOffer && discountEnabled && (
                    <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">
                      {PUBLIC_DISCOUNT_LABEL_LIVE}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Expert Advice add-on retired Feb-2026. Customers receive the
              plain-English Summary Report + Recommended Actions free of
              charge, and can request a free PWS proposal/assessment from
              the report when treatment is recommended. */}

          {/* Customer */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Customer</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name" required>
                <Input
                  data-testid="create-booking-name"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  className="mt-1"
                />
              </Field>
              <Field label="Business name (optional)">
                <Input
                  data-testid="create-booking-business-name"
                  value={customer.business_name || ""}
                  placeholder="e.g. real estate agency — shown on invoices"
                  onChange={(e) => setCustomer({ ...customer, business_name: e.target.value })}
                  className="mt-1"
                />
              </Field>
              <Field label="Email" required>
                <Input
                  data-testid="create-booking-email"
                  type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  className="mt-1"
                />
              </Field>
              <Field label="Mobile" required>
                <Input
                  data-testid="create-booking-mobile"
                  value={customer.mobile}
                  onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })}
                  className="mt-1"
                />
              </Field>
              <Field label="Sample type">
                <select
                  data-testid="create-booking-sample-type"
                  value={customer.sampleType}
                  onChange={(e) => setCustomer({ ...customer, sampleType: e.target.value })}
                  className="mt-1 w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white"
                >
                  {SAMPLE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">
                  Property / site address <span className="text-rose-500">*</span>
                </Label>
                <AddressAutocomplete
                  data-testid="create-booking-address"
                  value={customer.address}
                  onChange={(v) => setCustomer({ ...customer, address: v })}
                  placeholder="Start typing an Australian address…"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Phase 2L-A144 — Posted sample-kit picker (Feb 2026).
              Only renders when the `posted_kit_paid` feature flag is
              visible for the current staff user. Mirrors the public
              Booking flow's toggle so phone-in bookings capture the
              same delivery address + fee + Starshipit fulfilment
              hand-off. Hidden entirely when the flag is off — the
              server-side default (`kit_method="mail_out"`, no fee) is
              preserved for parity with legacy admin flows. */}
          {postedKit.enabled && (
            <div
              data-testid="create-booking-posted-kit-section"
              className="rounded-2xl bg-cyan-50/60 border border-cyan-200 p-4 space-y-3"
            >
              <div className="flex items-start gap-2">
                <Package className="w-4 h-4 mt-0.5 text-cyan-700" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">Sample kit</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Choose how this customer will get their sample bottle. Lab pickup / drop-off is free; posting a kit adds a prepaid Australia Post fee to the total.
                  </div>
                </div>
              </div>

              {/* Kit method toggle */}
              <div
                data-testid="create-booking-kit-method-toggle"
                className="grid grid-cols-1 sm:grid-cols-3 gap-2"
              >
                {[
                  {
                    key: "local_pickup_dropoff",
                    label: "Customer picks up bottle from Noosaville",
                    desc: "Customer collects a supplied bottle from the lab, or drops the sample in themselves.",
                    badge: "Free",
                    badgeTone: "bg-emerald-100 text-emerald-800",
                  },
                  {
                    key: "existing_bottle",
                    label: "Customer has kit / brings own bottle",
                    desc: "Customer already has a suitable sample kit or a clean 500 mL bottle. No lab dispatch required.",
                    badge: "Free",
                    badgeTone: "bg-emerald-100 text-emerald-800",
                  },
                  {
                    key: "mail_out",
                    label: "Post a kit to customer",
                    desc: "Aware posts a sterile kit with a prepaid return label.",
                    badge: `+$${(Number(postedKit.fee_cents) / 100).toFixed(2)} posted kit fee`,
                    badgeTone: "bg-cyan-100 text-cyan-800",
                  },
                  {
                    key: "technician_collected",
                    label: "Technician collects sample during service",
                    desc: "A Pristine/PWS technician takes the sample while already onsite. No dispatch, no self-sample discount.",
                    badge: "Internal",
                    badgeTone: "bg-slate-200 text-slate-700",
                  },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setKitMethod(opt.key)}
                    data-testid={`create-booking-kit-method-${opt.key}`}
                    className={`text-left rounded-xl border p-3 transition-all ${
                      kitMethod === opt.key
                        ? "border-cyan-600 bg-white ring-2 ring-cyan-600/20"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`mt-1 w-3 h-3 rounded-full border-2 flex-shrink-0 ${kitMethod === opt.key ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-[12px] text-slate-900 leading-snug">{opt.label}</div>
                          <span
                            data-testid={`create-booking-kit-method-${opt.key}-badge`}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap ${opt.badgeTone}`}
                          >
                            {opt.badge}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500 leading-snug">{opt.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Posted-kit address picker — only when mail_out */}
              {/* Jun 2026 — $20 self-sample discount */}
              <div data-testid="create-booking-self-sample-discount" className="pt-3 border-t border-cyan-200/60">
                <label className={`flex items-start gap-2.5 ${selfSampleEligible ? "cursor-pointer" : "opacity-60"}`}>
                  <input
                    type="checkbox"
                    data-testid="create-booking-self-sample-checkbox"
                    className="mt-0.5 w-4 h-4 accent-emerald-600"
                    checked={selfSampleEligible && selfSampleDiscount}
                    disabled={!selfSampleEligible}
                    onChange={(e) => setSelfSampleDiscount(e.target.checked)}
                  />
                  <span>
                    <span className="text-sm font-semibold text-slate-800">Apply $20 self-sample discount</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      {kitMethod === "technician_collected"
                        ? "Not available for technician-collected samples."
                        : kitMethod === "mail_out"
                          ? "Not available with mail-out kits — posted kits follow their own pricing."
                          : "Use when the customer is collecting/returning the sample themselves and no mail-out kit is required."}
                    </span>
                  </span>
                </label>
              </div>

              {kitMethod === "mail_out" && (
                <div className="pt-3 border-t border-cyan-200/60 space-y-3">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700">
                    Where should we post the kit?
                  </div>

                  {/* Address source toggle */}
                  <div
                    data-testid="create-booking-postal-source-toggle"
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                  >
                    {[
                      { key: "property", label: "Use the property / site address above", desc: "Reuses the address you entered for this booking." },
                      { key: "custom", label: "Different postal address", desc: "Search or type a separate delivery address." },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.key}
                        onClick={() => setPostalAddressSource(opt.key)}
                        data-testid={`create-booking-postal-source-${opt.key}`}
                        className={`text-left rounded-xl border p-2.5 transition-all ${
                          postalAddressSource === opt.key
                            ? "border-cyan-600 bg-white ring-2 ring-cyan-600/20"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`mt-1 w-3 h-3 rounded-full border-2 flex-shrink-0 ${postalAddressSource === opt.key ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`} />
                          <div className="flex-1">
                            <div className="font-semibold text-[12px] text-slate-900 leading-snug">{opt.label}</div>
                            <div className="mt-0.5 text-[11px] text-slate-500 leading-snug">{opt.desc}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {postalAddressSource === "property" && (
                    <div
                      data-testid="create-booking-postal-property-preview"
                      className="rounded-xl border border-cyan-200 bg-white p-3"
                    >
                      <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 mb-1">
                        Kit will be posted to
                      </div>
                      {customer.address ? (
                        <>
                          <div className="text-[13px] font-semibold text-slate-900 leading-snug break-words">
                            {customer.address}
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-slate-600">
                            <div>
                              <div className="uppercase tracking-wider font-semibold text-slate-500 text-[10px]">Suburb</div>
                              <div data-testid="create-booking-postal-preview-suburb">{effectivePostedKitAddress.suburb || "—"}</div>
                            </div>
                            <div>
                              <div className="uppercase tracking-wider font-semibold text-slate-500 text-[10px]">State</div>
                              <div data-testid="create-booking-postal-preview-state">{effectivePostedKitAddress.state || "—"}</div>
                            </div>
                            <div>
                              <div className="uppercase tracking-wider font-semibold text-slate-500 text-[10px]">Postcode</div>
                              <div data-testid="create-booking-postal-preview-postcode">{effectivePostedKitAddress.postcode || "—"}</div>
                            </div>
                          </div>
                          {(!effectivePostedKitAddress.suburb || !effectivePostedKitAddress.postcode) && (
                            <div className="mt-2 text-[11px] text-amber-700 leading-snug">
                              Couldn&rsquo;t parse a full AU suburb + postcode from the property address. Switch to &ldquo;Different postal address&rdquo; to enter it precisely.
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-[12px] text-rose-700 leading-snug">
                          No property address entered yet. Add the property address above, or choose &ldquo;Different postal address&rdquo;.
                        </div>
                      )}
                    </div>
                  )}

                  {postalAddressSource === "custom" && (
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Street address</Label>
                        <AddressAutocomplete
                          data-testid="create-booking-posted-kit-line1"
                          value={postedKitAddress.line1}
                          onChange={(v) => setPostalAddressFromAutocomplete(v, setPostedKitAddress)}
                          placeholder="Start typing the postal address…"
                          className="mt-1"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                          Address line 2 <span className="normal-case text-slate-400 tracking-normal">(optional)</span>
                        </Label>
                        <Input
                          data-testid="create-booking-posted-kit-line2"
                          value={postedKitAddress.line2}
                          onChange={(e) => setPostedKitAddress(a => ({ ...a, line2: e.target.value }))}
                          placeholder="Unit / building"
                          maxLength={200}
                          className="mt-1 h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Suburb</Label>
                        <Input
                          data-testid="create-booking-posted-kit-suburb"
                          value={postedKitAddress.suburb}
                          onChange={(e) => setPostedKitAddress(a => ({ ...a, suburb: e.target.value }))}
                          maxLength={120}
                          className="mt-1 h-10"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">State</Label>
                          <Input
                            data-testid="create-booking-posted-kit-state"
                            value={postedKitAddress.state}
                            onChange={(e) => setPostedKitAddress(a => ({ ...a, state: e.target.value.toUpperCase() }))}
                            placeholder="QLD"
                            maxLength={10}
                            className="mt-1 h-10"
                          />
                        </div>
                        <div>
                          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Postcode</Label>
                          <Input
                            data-testid="create-booking-posted-kit-postcode"
                            value={postedKitAddress.postcode}
                            onChange={(e) => setPostedKitAddress(a => ({ ...a, postcode: e.target.value }))}
                            placeholder="4566"
                            maxLength={10}
                            className="mt-1 h-10"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      Delivery notes <span className="normal-case text-slate-400 tracking-normal">(optional)</span>
                    </Label>
                    <Textarea
                      data-testid="create-booking-posted-kit-delivery-notes"
                      value={postedKitDeliveryNotes}
                      onChange={(e) => setPostedKitDeliveryNotes(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      placeholder="e.g. leave at back door, call before delivery"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phase 2L-A31 — Sample identification (optional). Mirrors the
              public booking form so multi-sample customers can label
              each sample; values flow through to labels + COA + lab
              queue search. */}
          <div
            data-testid="create-booking-sample-id-section"
            className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-3"
          >
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Sample identification (optional)</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                If the customer is booking more than one test, give each sample a label so you can tell them apart on the report.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Customer sample reference">
                <Input
                  data-testid="create-booking-customer-sample-reference"
                  value={customerSampleReference}
                  onChange={(e) => setCustomerSampleReference(e.target.value)}
                  placeholder="e.g. Tank A, Unit 2, Bore near shed"
                  maxLength={80}
                  className="mt-1"
                />
              </Field>
              <Field label="Where was this sample taken from?">
                <Input
                  data-testid="create-booking-sample-point"
                  value={samplePoint}
                  onChange={(e) => setSamplePoint(e.target.value)}
                  placeholder="e.g. Kitchen tap, Tank outlet, Post-filter"
                  maxLength={120}
                  className="mt-1"
                />
              </Field>
            </div>
          </div>

          {/* Phase 2L-A10 — Customer Questionnaire (mirrors public Booking flow) */}
          <div
            data-testid="create-booking-questionnaire-section"
            className={`rounded-2xl p-4 space-y-3 border ${skipQuestionnaire ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <ClipboardList className={`w-4 h-4 mt-0.5 ${skipQuestionnaire ? "text-amber-700" : "text-cyan-700"}`} />
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Customer questionnaire</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Same questions a customer answers on the public booking page. Feeds the Pristine Plain-English Summary and the BioCleanse® supplementary recommendation.
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer whitespace-nowrap">
                <input
                  data-testid="create-booking-skip-questionnaire"
                  type="checkbox"
                  checked={skipQuestionnaire}
                  onChange={(e) => setSkipQuestionnaire(e.target.checked)}
                  className="rounded"
                />
                Skip customer questions
              </label>
            </div>

            {skipQuestionnaire ? (
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-100/70 rounded-lg p-2.5">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Skipping means the Pristine Plain-English Summary won&rsquo;t show
                    a personalised &ldquo;reason for testing&rdquo; or BioCleanse® hygiene recommendation
                    for this booking. We&rsquo;ll record who skipped it and why in the booking audit trail.
                  </span>
                </div>
                <Field label="Reason for skipping" required>
                  <Textarea
                    data-testid="create-booking-skip-reason"
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    rows={2}
                    className="mt-1"
                    placeholder="e.g. Walk-in / phone-in — customer to complete questionnaire on lab receipt."
                    maxLength={500}
                  />
                </Field>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Reason for testing" required>
                    <select
                      data-testid="create-booking-reason-for-testing"
                      value={customer.reason_for_testing}
                      onChange={(e) => setCustomer({ ...customer, reason_for_testing: e.target.value })}
                      className="mt-1 w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white"
                    >
                      <option value="">Select a reason…</option>
                      {REASON_FOR_TESTING_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} data-testid={`create-booking-reason-opt-${o.value}`}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Primary water use" required>
                    <select
                      data-testid="create-booking-primary-water-use"
                      value={customer.primary_water_use}
                      onChange={(e) => setCustomer({ ...customer, primary_water_use: e.target.value })}
                      className="mt-1 w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white"
                    >
                      <option value="">Select the main use…</option>
                      {PRIMARY_WATER_USE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} data-testid={`create-booking-use-opt-${o.value}`}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {customer.reason_for_testing === "other" && (
                    <div className="sm:col-span-2">
                      <Field label="Please describe the reason for testing" required>
                        <Input
                          data-testid="create-booking-reason-for-testing-other"
                          value={customer.reason_for_testing_other}
                          onChange={(e) => setCustomer({ ...customer, reason_for_testing_other: e.target.value })}
                          placeholder="Briefly describe why the customer is testing the water"
                          className="mt-1"
                        />
                      </Field>
                    </div>
                  )}
                  {customer.primary_water_use === "other" && (
                    <div className="sm:col-span-2">
                      <Field label="Please describe the primary water use" required>
                        <Input
                          data-testid="create-booking-primary-water-use-other"
                          value={customer.primary_water_use_other}
                          onChange={(e) => setCustomer({ ...customer, primary_water_use_other: e.target.value })}
                          placeholder="Briefly describe how the water is used"
                          className="mt-1"
                        />
                      </Field>
                    </div>
                  )}
                </div>
                {/* Property & Water System Information — same component as
                    public Booking step 2. Drives the BioCleanse rule
                    server-side via `home_older_than_10y` + tank/filter answers. */}
                <div className="pt-3 border-t border-slate-200">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                    Property &amp; Water System Information
                  </div>
                  <div className="text-xs text-slate-600 mb-3 leading-relaxed">
                    A short stepper — same questions as the public booking page. Required so the BioCleanse® supplementary recommendation and AWARE Water Health Record enrichment can fire correctly.
                  </div>
                  <PropertyIntakeForm
                    value={intake}
                    onChange={setIntake}
                    testId={test.id}
                    embedded
                    testIdPrefix="create-booking-intake"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Payment + email */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Payment &amp; notifications</div>
            <Field label="Payment mode">
              <select
                data-testid="create-booking-payment-mode"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="mt-1 w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white"
              >
                {modes.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input
                data-testid="create-booking-send-email"
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <strong className="font-semibold">Send booking confirmation email to customer</strong>
                <span className="block text-xs text-slate-500 leading-relaxed mt-0.5">
                  Includes the sample label PDF attachment{paymentMode === "invoice" ? " and an 'invoice to follow' note" : paymentMode === "comp" ? " marked complimentary" : ""}. Uncheck to create the booking silently (admin manual follow-up).
                </span>
              </span>
            </label>
            <Field label="Internal notes (optional)">
              <Textarea
                data-testid="create-booking-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1"
                placeholder="Phone-in walk-up · sample dropped today · etc."
              />
            </Field>
          </div>

          {/* Discount code (Phase 2L-A5) — same validation source of truth
              as the public booking page: POST /api/discounts/validate */}
          <div data-testid="create-booking-discount-section" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Discount code</div>
            {!discount ? (
              <>
                <div className="flex gap-2">
                  <Input
                    data-testid="create-booking-discount-input"
                    value={discountInput}
                    onChange={(e) => {
                      setDiscountInput(e.target.value.toUpperCase());
                      if (discountErr) setDiscountErr("");
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyDiscount(); } }}
                    placeholder="Enter discount code"
                    className="h-10"
                    disabled={discountBusy}
                  />
                  <Button
                    data-testid="create-booking-discount-apply"
                    onClick={applyDiscount}
                    disabled={discountBusy || !discountInput.trim()}
                    variant="outline"
                    className="rounded-full font-semibold whitespace-nowrap"
                  >
                    {discountBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
                {discountErr && (
                  <div data-testid="create-booking-discount-error" className="text-xs text-rose-700 font-medium">
                    {discountErr}
                  </div>
                )}
                <div className="text-[11px] text-slate-500">
                  Uses the same validation as the public booking page — codes must be active, within their date window, and within usage limits.
                </div>
              </>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  <div data-testid="create-booking-discount-applied" className="font-semibold text-emerald-800">
                    {discount.code} applied · {discount.type === "percentage" ? `${discount.value}% off` : `$${Number(discount.value).toFixed(2)} off`}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    −${Number(discount.amount).toFixed(2)} off public booking price
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="create-booking-discount-clear"
                  onClick={clearDiscount}
                  className="text-xs font-semibold text-slate-600 hover:text-rose-700 underline"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Price summary */}
          <div className="bg-slate-900 text-white rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Standard package price</span>
              <span data-testid="create-booking-standard-price" className="tabular-nums font-semibold">
                ${standardPackagePrice.toFixed(2)}
              </span>
            </div>
            {publicBookingDiscountAmount > 0 && discountEnabled && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-emerald-300">{PUBLIC_DISCOUNT_LABEL_LIVE}</span>
                <span data-testid="create-booking-public-discount" className="tabular-nums font-semibold text-emerald-300">
                  −${publicBookingDiscountAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Public booking-page price</span>
              <span data-testid="create-booking-public-price" className="tabular-nums font-semibold">
                ${publicBookingPrice.toFixed(2)}
              </span>
            </div>
            {discount && discountCodeDiscountAmount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-emerald-300">Discount code ({discount.code})</span>
                <span data-testid="create-booking-discount-amount" className="tabular-nums font-semibold text-emerald-300">
                  −${discountCodeDiscountAmount.toFixed(2)}
                </span>
              </div>
            )}
            {postedKitFeeAmount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-cyan-200">Posted sample kit + prepaid return label</span>
                <span data-testid="create-booking-posted-kit-fee" className="tabular-nums font-semibold text-cyan-200">
                  +${postedKitFeeAmount.toFixed(2)}
                </span>
              </div>
            )}
            {selfSampleDiscountAmount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-emerald-300">Self-sample discount</span>
                <span data-testid="create-booking-self-sample-amount" className="tabular-nums font-semibold text-emerald-300">
                  −${selfSampleDiscountAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-slate-700">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300">Final amount payable</div>
                <div className="text-xs text-slate-400 mt-0.5">{test.name}</div>
              </div>
              <div data-testid="create-booking-total" className="text-2xl sm:text-3xl font-bold tabular-nums">
                ${finalAmountPayable.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} className="rounded-full">Cancel</Button>
          <Button
            data-testid="create-booking-submit"
            onClick={submit}
            disabled={submitting}
            className="rounded-full font-semibold"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
              : <><FilePlus className="w-4 h-4 mr-1.5" /> Create booking</>}
            {!submitting && sendEmail && <Mail className="w-3.5 h-3.5 ml-1.5 opacity-75" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CreateBookingModal;
